import { PropsWithChildren, useMemo, useState } from 'react';
import {
  AppointmentTypeGroup,
  BookableAppointmentTypeBulkUpdate,
} from '@weave/schema-gen-ts/dist/schemas/schedule/v3/bookable_appointment_type.pb';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { convertToCents, formatCentsToCurrency } from '@frontend/currency';
import { useTranslation } from '@frontend/i18n';
import {
  Modal,
  ModalControlModalProps,
  MoneyField,
  Table,
  TableColumnConfig,
  useAlert,
  useControlledField,
} from '@frontend/design-system';

export const IndividualPrepayAmountUpdate = ({
  appointmentTypeGroups,
  locationId,
  modalProps,
  children,
}: PropsWithChildren<{
  appointmentTypeGroups: AppointmentTypeGroup[];
  locationId: string;
  modalProps: ModalControlModalProps;
}>) => {
  const { t } = useTranslation('scheduleServiceTypes');
  const alerts = useAlert();
  const [updatedAppointmentTypeGroups, setUpdatedAppointmentTypeGroups] =
    useState<AppointmentTypeGroup[]>(appointmentTypeGroups);
  const hasChanges = useMemo(
    () =>
      updatedAppointmentTypeGroups.some((updatedGroup, index) => {
        const originalGroup = appointmentTypeGroups[index];
        const originalBookableAppointmentType = originalGroup.bookableAppointmentTypes?.[0];
        const updatedBookableAppointmentType = updatedGroup.bookableAppointmentTypes?.[0];
        return (
          Number(updatedBookableAppointmentType?.prepaymentAmount || 0) !==
          Number(originalBookableAppointmentType?.prepaymentAmount || 0)
        );
      }),
    [updatedAppointmentTypeGroups, appointmentTypeGroups]
  );

  const tableConfig = useMemo(
    (): TableColumnConfig<AppointmentTypeGroup>[] => [
      {
        Header: t('Appointment Type'),
        id: 'displayName',
        accessor: (row) => row?.appointmentType?.displayName || '-',
        minWidth: 200,
        maxWidth: 400,
        disableSortBy: true,
      },
      {
        Header: t('Amount'),
        id: 'prepaymentAmount',
        minWidth: 100,
        maxWidth: 200,
        disableSortBy: true,
        accessor: (row) => row,
        cellRenderer: (row: AppointmentTypeGroup) => (
          <PrepayAmountField row={row} setUpdatedAppointmentTypeGroups={setUpdatedAppointmentTypeGroups} />
        ),
      },
    ],
    [t]
  );

  const { mutateAsync } = SchedulerV3.Mutations.useBulkUpdateBookableAppointmentTypesMutation();
  const handleUpdateAmounts = async () => {
    try {
      await mutateAsync({
        updateType: 'PREPAYMENT_AMOUNT',
        locationId,
        updates: updatedAppointmentTypeGroups.reduce((acc, at) => {
          const bookableAppointmentType = at.bookableAppointmentTypes?.[0];
          const originalBookableAppointmentType = appointmentTypeGroups.find(
            (group) => group.appointmentType?.id === at.appointmentType?.id
          )?.bookableAppointmentTypes?.[0];
          const updatedAmount = Number(bookableAppointmentType?.prepaymentAmount || 0);
          const originalAmount = Number(originalBookableAppointmentType?.prepaymentAmount || 0);
          if (bookableAppointmentType && updatedAmount !== originalAmount) {
            acc.push({
              bookableAppointmentTypeId: bookableAppointmentType.id,
              prepaymentAmount: updatedAmount,
            });
          }
          return acc;
        }, [] as BookableAppointmentTypeBulkUpdate[]),
      });

      modalProps.onClose();
    } catch (error) {
      alerts.error(t('Failed to update pre-pay amounts'));
    }
  };

  return (
    <>
      <Modal.Body>
        {children}
        <Table colConfig={tableConfig} data={updatedAppointmentTypeGroups} />
      </Modal.Body>
      <Modal.Footer
        primary={{
          label: t('Update Amounts'),
          onClick: handleUpdateAmounts,
          disabled: !hasChanges,
        }}
        secondary={{
          label: t('Reset'),
          onClick: () => setUpdatedAppointmentTypeGroups(appointmentTypeGroups),
          disabled: !hasChanges,
        }}
      />
    </>
  );
};

const PrepayAmountField = ({
  row,
  setUpdatedAppointmentTypeGroups,
}: {
  row: AppointmentTypeGroup;
  setUpdatedAppointmentTypeGroups: React.Dispatch<React.SetStateAction<AppointmentTypeGroup[]>>;
}) => {
  const { t } = useTranslation('scheduleServiceTypes');
  const bookableAppointmentType = row?.bookableAppointmentTypes?.[0];

  const amount = formatCentsToCurrency(bookableAppointmentType?.prepaymentAmount);

  const prepayAmountFieldProps = useControlledField({
    type: 'money',
    value: amount,
    onChange: (value) => {
      const numValue = isNaN(Number(value)) ? 0 : Number(value);
      setUpdatedAppointmentTypeGroups((prev) =>
        prev.map((atg) =>
          atg.appointmentType?.id === row.appointmentType?.id
            ? {
                ...atg,
                bookableAppointmentTypes: atg.bookableAppointmentTypes?.map((bat) => ({
                  ...bat,
                  prepaymentAmount: convertToCents(numValue),
                })),
              }
            : atg
        )
      );
    },
    required: false,
  });

  // money field defaults to an error message for 0 value, but we want to allow that for prepay amounts, so we override the error message logic here
  const errorMsg = useMemo(() => {
    const value = (prepayAmountFieldProps.value ?? '').replace(/[$,]/g, ''); // Remove $ and commas for validation
    if (isNaN(Number(value))) {
      return t('Please enter a valid number');
    }
    if (Number(value) < 0) {
      return t('Amount cannot be negative');
    }
    return '';
  }, [prepayAmountFieldProps.value, t]);

  return (
    <MoneyField
      name={`prepay-amount-${bookableAppointmentType?.id}`}
      label={''}
      hideErrorText={prepayAmountFieldProps.error === ''}
      disabled={!bookableAppointmentType}
      placeholder='$'
      {...prepayAmountFieldProps}
      error={errorMsg}
    />
  );
};
